import { Stack } from '@mui/material';
import {
  EscalationEvaluationContext,
  formatResponseExpectation,
  matchEscalationRule,
  parseEscalationRules,
} from '@provision/sidekick-utils';
import { ChevronDown } from 'lucide-react';
import { useCallback, useMemo } from 'react';
import { ROW_CONTENT_PAD_Y } from 'src/components/Assistant/policies-tab/policy-card.styles';
import { PolicyCategoryGroupSection } from 'src/components/Assistant/policies-tab/policy-category-group';
import { POLICY_CATEGORY_ESCALATION } from 'src/components/Assistant/policies-tab/policy-constants';
import { PolicyGlyph } from 'src/components/Assistant/policies-tab/policy-glyph';
import { groupPoliciesByCategory } from 'src/components/Assistant/policies-tab/policy-groups';
import { sortPoliciesForDisplay } from 'src/components/Assistant/policies-tab/policy-order';
import { getSeverityForType } from 'src/components/Assistant/policies-tab/policy-severity';
import { useApplicablePolicies } from 'src/components/Assistant/policies-tab/use-applicable-policies';
import { usePolicyCategoryOpen } from 'src/components/Assistant/policies-tab/use-policy-category-open';
import { useSidekickTransition } from 'src/components/Navbar/DesktopDrawer/SidekickPane/use-sidekick-transition';
import { useScrollEdges } from 'src/components/Navbar/DesktopDrawer/use-scroll-edges';
import { PLATFORM_PARTNER_ID } from 'src/constants/platform';
import { useGroupRules, UserGroupRules } from 'src/graphql';
import { NormalizedSupportPolicy } from 'src/graphql/entities/support-policy/support-policy.utils';
import { useGlobalStore } from 'src/store';
import useNavigationStore from 'src/store/navigation';
import { getDisplayedTextFromHtml } from 'src/utils/text-parsing';
import {
  getStageSx,
  PolicyRow,
  PreviewCard,
  PreviewLabel,
  RowBody,
  RowDesc,
  RowName,
  RowTitle,
  ScrollCue,
} from './active-policies-preview.styles';

const SCROLL_EDGE_TOLERANCE_PX = 2;
const SCROLL_CUE_OVERLAP_PX = 28;

/**
 * Collapsed-row copy for a policy. Escalation policies use the same
 * `formatResponseExpectation` path as the Sidekick policies tab; every other
 * category keeps its HTML-stripped description.
 */
const getPolicyPreviewSummary = (
  policy: Pick<
    NormalizedSupportPolicy,
    'category' | 'description' | 'escalationRules' | 'urgencyLevel'
  >,
  evaluation: {
    escalationEval: ReturnType<typeof useApplicablePolicies>['escalationEval'];
    policyEvaluationContext: ReturnType<
      typeof useApplicablePolicies
    >['policyEvaluationContext'];
  },
): string => {
  if (policy.category !== POLICY_CATEGORY_ESCALATION) {
    return getDisplayedTextFromHtml(policy.description);
  }

  const { escalationEval, policyEvaluationContext } = evaluation;
  const escalationCutoffHours = policy.urgencyLevel
    ? escalationEval.getEscalationCutoffHoursForUrgency(policy.urgencyLevel)
    : policyEvaluationContext.escalationCutoffHours;

  const context: EscalationEvaluationContext = {
    currentTime: policyEvaluationContext.currentTime ?? new Date(),
    timezone: policyEvaluationContext.timezone,
    businessHours: policyEvaluationContext.businessHours,
    escalationCutoffHours,
    holidays: policyEvaluationContext.holidays,
    isPreLits: escalationEval.isPreLits,
  };

  const match = matchEscalationRule(
    parseEscalationRules(policy.escalationRules),
    escalationEval.serviceLevel,
    context,
  );

  return formatResponseExpectation(
    match,
    escalationEval.serviceLevel,
    policyEvaluationContext.timezone,
  );
};

/** A launcher, not a second surface: a row opens the full Policies panel. */
export const ActivePoliciesPreview = () => {
  const { checkValidity, loading: rulesLoading } = useGroupRules();
  const currentPartnerId = useGlobalStore.use.selectedPartnerId();
  const setActivePane = useNavigationStore((state) => state.setActivePane);
  const requestSidekick = useNavigationStore((state) => state.requestSidekick);
  const { hasTransitioned, completeTransition } = useSidekickTransition();

  const canSeePreview = !rulesLoading && checkValidity([UserGroupRules.AccessToPolicyPreview]);

  const { applicablePolicies, policyEvaluationContext, escalationEval } =
    useApplicablePolicies({
      fallbackPartnerId: currentPartnerId ?? PLATFORM_PARTNER_ID,
    });

  const groups = useMemo(
    () =>
      groupPoliciesByCategory(sortPoliciesForDisplay(applicablePolicies)).map((group) => ({
        ...group,
        rows: group.policies.map((policy) => ({
          id: policy.id,
          title: policy.title,
          summary: getPolicyPreviewSummary(policy, {
            escalationEval,
            policyEvaluationContext,
          }),
          severity: getSeverityForType(policy.type),
          glyph: {
            category: policy.category,
            type: policy.type,
            urgencyLevel: policy.urgencyLevel,
          },
        })),
      })),
    [applicablePolicies, policyEvaluationContext, escalationEval],
  );

  const categoryOpen = usePolicyCategoryOpen(groups);

  // past its height cap the stage stops growing, so neither folding a group nor
  // gaining a row reaches the ResizeObserver — the fades need both spelled out
  const stageSignature = groups
    .map(
      (group) =>
        `${group.category}:${categoryOpen.isOpen(group.category) ? 1 : 0}:${group.rows.length}`,
    )
    .join('|');

  const {
    ref: stageRef,
    edges,
    syncEdges,
  } = useScrollEdges<HTMLDivElement>({
    axis: 'vertical',
    tolerancePx: SCROLL_EDGE_TOLERANCE_PX,
    resyncOn: stageSignature,
  });

  const openPolicy = useCallback(
    (policyId: string) => {
      requestSidekick({ subtab: 'policies', policyId });
      // else the old bottom bar keeps sitting under the pane it just opened
      if (!hasTransitioned) {
        completeTransition();
      }
      setActivePane('sidekick');
    },
    [requestSidekick, hasTransitioned, completeTransition, setActivePane],
  );

  if (!canSeePreview || groups.length === 0) {
    return null;
  }

  return (
    <PreviewCard data-testid="active-policies-preview">
      <PreviewLabel>Active policies</PreviewLabel>

      <Stack ref={stageRef} onScroll={syncEdges} sx={getStageSx(edges.start, edges.end)}>
        {groups.map((group, index) => (
          <PolicyCategoryGroupSection
            key={group.category}
            category={group.category}
            total={group.policies.length}
            restrictionCount={group.restrictionCount}
            open={categoryOpen.isOpen(group.category)}
            onToggle={() => categoryOpen.toggle(group.category)}
            isFirst={index === 0}
            onTransitionEnd={syncEdges}
          >
            {group.rows.map((row) => (
              <PolicyRow
                key={row.id}
                data-testid="active-policies-preview-row"
                data-policy-id={row.id}
                type="button"
                disableRipple
                severity={row.severity}
                onClick={() => openPolicy(row.id)}
              >
                <PolicyGlyph policy={row.glyph} extraOffsetY={ROW_CONTENT_PAD_Y} />
                <RowBody>
                  <RowName>
                    <RowTitle>{row.title}</RowTitle>
                  </RowName>
                  {row.summary ? <RowDesc>{row.summary}</RowDesc> : null}
                </RowBody>
              </PolicyRow>
            ))}
          </PolicyCategoryGroupSection>
        ))}
      </Stack>

      {edges.end ? (
        <ScrollCue
          type="button"
          aria-label="Scroll for more policies"
          onClick={() =>
            stageRef.current?.scrollBy({
              top: stageRef.current.clientHeight - SCROLL_CUE_OVERLAP_PX,
              behavior: 'smooth',
            })
          }
        >
          <ChevronDown size={15} />
        </ScrollCue>
      ) : null}
    </PreviewCard>
  );
};
